import React, { useContext, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
	AccordionDetails,
	AccordionSummary,
	Chip,
	CircularProgress,
	Pagination,
	Stack,
	Typography,
} from "@mui/material";
import { Reservation } from "../../../types/entities";
import { ReservationApi } from "../../../api/reservationApi";
import {
	MultistepContext,
	MultistepContextValues,
} from "../../../types/context";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EmojiPeopleIcon from "@mui/icons-material/EmojiPeople";
import { LangContext } from "../../../types/internationalization";
import { ReservationAccordion } from "../../styled/profileComponents";
import { DetailTypography } from "../../styled/detailsComponents";
import { theme } from "../../../styles/themeProvider";
import { CancelButton } from "../../buttons/CancelButton";
import { useWindowResize } from "../../../hooks/useWindowResize";
import { dateToString } from "../../../utils/dateFormat";
import { ContactNumber } from "../../styled/ContactNumber";

export const MyReservations = () => {
	const { formatMessage } = useIntl();
	const { isMobile } = useWindowResize();

	const { reservation } = useContext(
		MultistepContext
	) as MultistepContextValues;
	const langContext = useContext(LangContext);
	const locale = langContext!.locale;

	const [cancelationStates, setCancelationStates] = useState<{
		[id: string]: {
			cancelationMade: boolean;
			loading: boolean;
			error: boolean;
		};
	}>({});

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [expanded, setExpanded] = useState<string | false>(false);

	const [customerReservations, setCustomerReservations] = useState<
		Reservation[]
	>([]);
	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const reservationsPerPage = 5;

	useEffect(() => {
		const fetchData = async () => {
			try {
				const reservationApi = new ReservationApi();
				const response = await reservationApi.getCustomerReservations(
					reservation.customer.id!,
					reservationsPerPage,
					page
				);
				setTotalPages(
					Math.ceil(response.data.totalItems / reservationsPerPage)
				);
				setCustomerReservations(response.data.items);
			} catch (error) {
				console.error("Error getting reservations:", error);
			} finally {
				setIsLoading(false);
			}
		};
		setIsLoading(true);
		fetchData();
	}, [reservation.customer.id, page]);

	const handleCancel = async (id: string) => {
		try {
			setCancelationStates((prevState) => ({
				...prevState,
				[id]: {
					...prevState[id],
					loading: true,
					error: false,
					cancelationMade: false,
				},
			}));

			const reservationApi = new ReservationApi();
			await reservationApi.cancelReservation(id);

			setCancelationStates((prevState) => ({
				...prevState,
				[id]: {
					...prevState[id],
					loading: false,
					error: false,
					cancelationMade: true,
				},
			}));
		} catch (error) {
			setCancelationStates((prevState) => ({
				...prevState,
				[id]: {
					...prevState[id],
					loading: false,
					error: true,
					cancelationMade: false,
				},
			}));
		} finally {
			setCancelationStates((prevState) => ({
				...prevState,
				[id]: {
					...prevState[id],
					loading: false,
				},
			}));
		}
	};

	const handleChangePage = (
		_event: React.ChangeEvent<unknown>,
		value: number
	) => {
		setPage(value);
	};

	const handleAccordionChange =
		(panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
			setExpanded(isExpanded ? panel : false);
		};

	return (
		<Stack
			paddingTop={4}
			paddingBottom={2}
			width="100%"
			maxWidth="500px"
			alignItems="center"
		>
			{isLoading ? (
				<CircularProgress />
			) : !customerReservations || customerReservations.length === 0 ? (
				<Typography>
					<FormattedMessage
						id="profile.emptyReservations"
						defaultMessage="Ups! No hay reservas para mostrar aún."
					/>
				</Typography>
			) : (
				<>
					{customerReservations.map((reservation) => (
						<ReservationAccordion
							key={reservation.id}
							disabled={
								reservation.state!.name === "CANCELLED" ||
								cancelationStates[reservation.id!]?.cancelationMade
							}
							expanded={
								expanded === reservation.id &&
								reservation.state!.name !== "CANCELLED" &&
								!cancelationStates[reservation.id!]?.cancelationMade
							} // Despliega solo si no está cancelada
							onChange={handleAccordionChange(reservation.id!)}
						>
							<AccordionSummary expandIcon={<ExpandMoreIcon />}>
								<Stack
									width="97%"
									direction="row"
									justifyContent="space-between"
									alignItems="center"
									spacing={1}
								>
									<Typography fontWeight={600} flex={1}>
										{dateToString(new Date(reservation.time!), locale)}
									</Typography>
									{reservation.state!.name === "CANCELLED" ||
									cancelationStates[reservation.id!]?.cancelationMade ? (
										<Chip
											label={formatMessage({
												id: "profile.canceled",
												defaultMessage: "Cancelada",
											})}
											color="error"
										/>
									) : (
										reservation.state!.name === "PENDING" && (
											<Chip
												label={formatMessage({
													id: "profile.pending",
													defaultMessage: "Pago pendiente",
												})}
												color="warning"
											/>
										)
									)}
								</Stack>
							</AccordionSummary>
							<AccordionDetails>
								<Stack direction="row" spacing={2}>
									<Stack
										width="100%"
										direction="row"
										justifyContent="space-around"
										sx={{
											backgroundColor: theme.palette.secondary.light,
											borderRadius: "10px",
											paddingY: "12px",
										}}
									>
										<DetailTypography>
											{isMobile && (
												<EmojiPeopleIcon
													fontSize="small"
													sx={{ mb: "-7px", pr: "5px" }}
												/>
											)}
											{`${reservation.numberDiners} `}
											{!isMobile &&
												`${formatMessage({
													id: "details.diner",
													defaultMessage: "persona",
												})}${reservation.numberDiners > 1 ? "s" : ""}`}
										</DetailTypography>
										<DetailTypography>
											{new Date(reservation.time!).toLocaleTimeString(
												langContext?.locale,
												{
													hour: "2-digit",
													minute: "2-digit",
												}
											)}
										</DetailTypography>
									</Stack>
									<Stack direction="row">
										<CancelButton
											handleCancel={() => handleCancel(reservation.id!)}
										>
											{cancelationStates[reservation.id!]?.loading ? (
												<CircularProgress size={24} color="error" />
											) : (
												formatMessage({
													id: "profile.cancelReservation",
													defaultMessage: "Cancelar",
												})
											)}
										</CancelButton>
									</Stack>
								</Stack>
								{reservation.state!.name === "PENDING" && (
									<Typography
										fontWeight={400}
										marginTop={3}
										textAlign="center"
										fontSize="14px"
									>
										<FormattedMessage
											id="profile.payFeeMessage"
											defaultMessage="Si desea consultar el pago de la seña, por favor comunicarse al"
										/>{" "}
										<ContactNumber />
									</Typography>
								)}
							</AccordionDetails>
						</ReservationAccordion>
					))}
					<Pagination
						count={totalPages}
						page={page}
						onChange={handleChangePage}
						color="primary"
						sx={{
							paddingTop: 1.5,
							"& .MuiPaginationItem-root.Mui-selected": {
								backgroundColor: "transparent",
								color: theme.palette.primary.main,
								fontWeight: 500,
								border: `1px solid ${theme.palette.primary.main}`,
							},
						}}
					/>
					<Stack
						direction="row"
						width="100%"
						justifyContent="center"
						alignContent="center"
					>
						<Typography fontWeight={400} marginTop={3} textAlign="center">
							<FormattedMessage
								id="profile.editReservationMessage"
								defaultMessage="Si desea modificar una reserva, por favor comunicarse al"
							/>{" "}
							<ContactNumber />
						</Typography>
					</Stack>
				</>
			)}
		</Stack>
	);
};
